import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  Payment,
  Download,
  Visibility,
  AccountBalance,
  CreditCard,
  PaymentOutlined,
  Euro,
  TrendingUp,
} from '@mui/icons-material';
import { DataService } from '../services/dataService';
import { Commission, CommissionStatus } from '../types';

const CommissionsScreen: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<Commission | null>(null);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const { data: commissions, isLoading, error } = useQuery<Commission[]>({
    queryKey: ['commissions'],
    queryFn: DataService.getCommissions,
  });

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
  };

  const handleDateFilterChange = (event: SelectChangeEvent) => {
    setDateFilter(event.target.value);
  };

  const handlePaymentClick = (commission: Commission) => {
    setSelectedCommission(commission);
    setPaymentDialogOpen(true);
  };

  const handlePaymentClose = () => {
    setPaymentDialogOpen(false);
    setSelectedCommission(null);
  };

  const handlePaymentSubmit = (paymentMethod: string) => {
    // TODO: Implement payment logic
    console.log(`Processing payment for commission ${selectedCommission?.id} via ${paymentMethod}`);
    handlePaymentClose();
  };

  const handleExcelExport = async () => {
    if (!commissions || commissions.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    setIsExportingExcel(true);
    try {
      // Create CSV content
      const headers = [
        'ID Commission',
        'Parrain',
        'Agence',
        'Montant',
        'Statut',
        'Date de création',
        'Date de paiement',
        'Méthode de paiement'
      ];

      const csvContent = [
        headers.join(','),
        ...filteredCommissions.map(commission => [
          commission.id,
          commission.recommenderName,
          commission.agencyName,
          commission.amount.toFixed(2),
          commission.status,
          commission.createdAt.toLocaleDateString('fr-FR'),
          commission.paidAt ? commission.paidAt.toLocaleDateString('fr-FR') : '',
          commission.paymentMethod || ''
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `commissions_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      // Show success message
      alert(`Export Excel réussi ! ${filteredCommissions.length} commissions exportées.`);
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      alert('Erreur lors de l\'export Excel. Veuillez réessayer.');
    } finally {
      setIsExportingExcel(false);
    }
  };

  const handlePDFExport = async () => {
    if (!commissions || commissions.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    setIsExportingPDF(true);
    try {
      // Create a simple HTML table for PDF generation
      const tableHTML = `
        <html>
          <head>
            <title>Rapport des Commissions</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              .header { text-align: center; margin-bottom: 20px; }
              .summary { margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Rapport des Commissions</h1>
              <p>Généré le: ${new Date().toLocaleDateString('fr-FR')}</p>
            </div>
            
            <div class="summary">
              <h3>Résumé</h3>
              <p>Total des commissions: ${filteredCommissions.length}</p>
              <p>Montant total: ${filteredCommissions.reduce((sum, c) => sum + c.amount, 0).toFixed(2)} €</p>
              <p>En attente: ${totalPending} (${totalPendingAmount.toFixed(2)} €)</p>
              <p>Validées: ${totalValidated} (${totalValidatedAmount.toFixed(2)} €)</p>
              <p>Payées: ${totalPaid}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>Parrain</th>
                  <th>Agence</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th>Date de création</th>
                </tr>
              </thead>
              <tbody>
                ${filteredCommissions.map(commission => `
                  <tr>
                    <td>${commission.recommenderName}</td>
                    <td>${commission.agencyName}</td>
                    <td>${commission.amount.toFixed(2)} €</td>
                    <td>${commission.status}</td>
                    <td>${commission.createdAt.toLocaleDateString('fr-FR')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </body>
        </html>
      `;

      // Open in new window for printing/PDF generation
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(tableHTML);
        newWindow.document.close();
        newWindow.focus();
        
        // Show success message
        alert(`Export PDF réussi ! ${filteredCommissions.length} commissions exportées. Utilisez Ctrl+P pour imprimer ou sauvegarder en PDF.`);
        
        // Auto-print for PDF generation
        setTimeout(() => {
          newWindow.print();
        }, 1000);
      } else {
        alert('Impossible d\'ouvrir la fenêtre d\'impression. Veuillez vérifier vos bloqueurs de popup.');
      }
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    } finally {
      setIsExportingPDF(false);
    }
  };

  const filteredCommissions = commissions?.filter(commission => {
    const matchesSearch = commission.recommenderName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         commission.agencyName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || commission.status === statusFilter;
    const matchesDate = dateFilter === 'all' || true; // TODO: Implement date filtering
    return matchesSearch && matchesStatus && matchesDate;
  }) || [];

  // Calculate summary statistics
  const totalPending = filteredCommissions.filter(c => c.status === CommissionStatus.PENDING).length;
  const totalValidated = filteredCommissions.filter(c => c.status === CommissionStatus.VALIDATED).length;
  const totalPaid = filteredCommissions.filter(c => c.status === CommissionStatus.PAID).length;
  const totalPendingAmount = filteredCommissions
    .filter(c => c.status === CommissionStatus.PENDING)
    .reduce((sum, c) => sum + c.amount, 0);

  const totalValidatedAmount = filteredCommissions
    .filter(c => c.status === CommissionStatus.VALIDATED)
    .reduce((sum, c) => sum + c.amount, 0);

  const getStatusColor = (status: CommissionStatus) => {
    switch (status) {
      case CommissionStatus.PENDING: return 'warning';
      case CommissionStatus.VALIDATED: return 'info';
      case CommissionStatus.PAID: return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: CommissionStatus) => {
    switch (status) {
      case CommissionStatus.PENDING: return 'En attente';
      case CommissionStatus.VALIDATED: return 'Validée';
      case CommissionStatus.PAID: return 'Payée';
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Erreur lors du chargement des commissions</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Commissions & Paiements</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={isExportingExcel ? <div>⏳</div> : <Download />} 
            onClick={handleExcelExport}
            disabled={isExportingExcel || isExportingPDF}
          >
            {isExportingExcel ? 'Export...' : 'Exporter Excel'}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={isExportingPDF ? <div>⏳</div> : <Download />} 
            onClick={handlePDFExport}
            disabled={isExportingExcel || isExportingPDF}
          >
            {isExportingPDF ? 'Export...' : 'Exporter PDF'}
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" color="warning.main" gutterBottom>
            {totalPending}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            En attente
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" color="info.main" gutterBottom>
            {totalValidated}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Validées
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main" gutterBottom>
            {totalPaid}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Payées
          </Typography>
        </Paper>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" color="primary.main" gutterBottom>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalPendingAmount)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Montant en attente
          </Typography>
        </Paper>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Rechercher une commission..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={handleStatusFilterChange}
            >
              <MenuItem value="all">Tous les statuts</MenuItem>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="validated">Validée</MenuItem>
              <MenuItem value="paid">Payée</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Période</InputLabel>
            <Select
              value={dateFilter}
              label="Période"
              onChange={handleDateFilterChange}
            >
              <MenuItem value="all">Toutes les périodes</MenuItem>
              <MenuItem value="today">Aujourd'hui</MenuItem>
              <MenuItem value="week">Cette semaine</MenuItem>
              <MenuItem value="month">Ce mois</MenuItem>
              <MenuItem value="quarter">Ce trimestre</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Commissions Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Parrain</TableCell>
                <TableCell>Agence</TableCell>
                <TableCell>Montant</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Date de création</TableCell>
                <TableCell>Date de paiement</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCommissions.map((commission) => (
                <TableRow key={commission.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar>{commission.recommenderName[0]}</Avatar>
                      <Typography variant="subtitle2">
                        {commission.recommenderName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {commission.agencyName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(commission.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusLabel(commission.status)} 
                      color={getStatusColor(commission.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {commission.createdAt.toLocaleDateString('fr-FR')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {commission.paidAt ? (
                      <Typography variant="body2">
                        {commission.paidAt.toLocaleDateString('fr-FR')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Voir détails">
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {commission.status === CommissionStatus.VALIDATED && (
                        <Tooltip title="Payer">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handlePaymentClick(commission)}
                          >
                            <Payment />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small">
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={handlePaymentClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Paiement de la commission
        </DialogTitle>
        <DialogContent>
          {selectedCommission && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Parrain:</strong> {selectedCommission.recommenderName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Agence:</strong> {selectedCommission.agencyName}
              </Typography>
              <Typography variant="h6" color="primary" gutterBottom>
                <strong>Montant:</strong> {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(selectedCommission.amount)}
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Choisir le mode de paiement:
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CreditCard />}
                  onClick={() => handlePaymentSubmit('stripe')}
                  sx={{ p: 2 }}
                >
                  Stripe
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PaymentOutlined />}
                  onClick={() => handlePaymentSubmit('paypal')}
                  sx={{ p: 2 }}
                >
                  Paiement
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AccountBalance />}
                  onClick={() => handlePaymentSubmit('bank')}
                  sx={{ p: 2 }}
                >
                  Virement
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentClose}>Annuler</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionsScreen;
